/**
 * Sortable list helpers for the Resources page and its section editor.
 *
 * Reordering happens from a drag handle, so the inputs and links in a row keep
 * their normal clicks. The keyboard sensor makes the handle work without a
 * mouse: Space to lift, arrows to move, Space to drop.
 */

import type { CSSProperties, ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableListProps {
  ids: string[];
  onMove: (oldIndex: number, newIndex: number) => void;
  children: ReactNode;
}

export function SortableList({ ids, onMove, children }: SortableListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const oldIndex = ids.indexOf(String(e.active.id));
    const newIndex = ids.indexOf(String(e.over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onMove(oldIndex, newIndex);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

interface SortableItemProps {
  id: string;
  /** Names the row for screen readers, e.g. "Reorder Getting Paid". */
  handleLabel: string;
  className?: string;
  children: (handle: ReactNode) => ReactNode;
}

export function SortableItem({ id, handleLabel, className = '', children }: SortableItemProps) {
  const {
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
  } = useSortable({ id });

  const style: CSSProperties = {
    // Translate, not Transform: dnd-kit also scales the dragged node to the
    // size of whatever it is over, so a short section over a tall one balloons
    // and a tall one over a short one collapses.
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: isDragging ? 'relative' : undefined,
  };

  const handle = (
    <button
      type="button"
      aria-label={handleLabel}
      className="-ml-1 w-8 h-8 shrink-0 flex items-center justify-center text-ink-muted hover:text-ink cursor-grab active:cursor-grabbing focus-ring touch-none"
      {...attributes}
      {...listeners}
      ref={setActivatorNodeRef}
    >
      <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <circle cx="7" cy="5" r="1.5" />
        <circle cx="13" cy="5" r="1.5" />
        <circle cx="7" cy="10" r="1.5" />
        <circle cx="13" cy="10" r="1.5" />
        <circle cx="7" cy="15" r="1.5" />
        <circle cx="13" cy="15" r="1.5" />
      </svg>
    </button>
  );

  return (
    <div ref={setNodeRef} style={style} className={`${className} ${isDragging ? 'shadow-float' : ''}`}>
      {children(handle)}
    </div>
  );
}
